import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, ArrowLeft, Loader } from 'lucide-react';
import certificateAPI from '../services/certificateAPI';
import { toast } from 'react-hot-toast';

const CertificateViewer = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [certificateHTML, setCertificateHTML] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCertificate();
  }, [token]);

  const loadCertificate = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch certificate from backend
      const response = await fetch(`${import.meta.env.VITE_API_URL || process.env.RAILWAY_PUBLIC_DOMAIN || 'http://localhost:3001'}/api/certificates/${token}`);
      
      if (!response.ok) {
        throw new Error('Certificate not found or expired');
      }
      
      const html = await response.text();
      setCertificateHTML(html);
    } catch (error) {
      console.error('Error loading certificate:', error);
      setError(error.message);
      toast.error('Failed to load certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await certificateAPI.downloadCertificate(token);
      toast.success('Certificate downloaded successfully!');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  const handleBack = () => {
    navigate('/certificates');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <h2 className="font-bold text-lg mb-2">Certificate Not Found</h2>
            <p>{error}</p>
          </div>
          <button
            onClick={handleBack}
            className="btn-gradient flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Certificates</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with controls */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Certificates</span>
          </button>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={handleDownload}
              className="btn-gradient flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download Certificate</span>
            </button>
          </div>
        </div>
      </div>

      {/* Certificate content */}
      <div className="max-w-7xl mx-auto p-6">
        <div 
          className="bg-white rounded-lg shadow-lg overflow-hidden"
          dangerouslySetInnerHTML={{ __html: certificateHTML }}
        />
      </div>
    </div>
  );
};

export default CertificateViewer;
